import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface TenantOverview {
  id: string;
  name: string;
  slug: string;
  agentRuntimeEnabled: boolean;
  createdAt: string;
}

interface PlatformStats {
  totalTenants: number;
  totalUsers: number;
}

export function PlatformAdminPage() {
  const { t } = useTranslation();
  const [tenants, setTenants] = useState<TenantOverview[]>([]);
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [tenantsRes, statsRes] = await Promise.all([
        apiClient.get<TenantOverview[]>('/platform/tenants'),
        apiClient.get<PlatformStats>('/platform/stats'),
      ]);
      setTenants(tenantsRes.data);
      setStats(statsRes.data);
      setError(null);
    } catch {
      setError('Access denied or failed to load. Platform Admin role required.');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (error) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold">Platform Administration</h1>
        <div className="rounded-lg border border-red-800 bg-red-950/40 p-4 text-sm text-red-300">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <CreateTenantModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={load}
      />

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Platform Administration</h1>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          + Tenant
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard label="Tenants" value={stats.totalTenants} />
          <StatCard label="Users" value={stats.totalUsers} />
        </div>
      )}

      <div className="rounded-lg border bg-card">
        <div className="border-b px-4 py-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            All Tenants
          </h2>
        </div>
        <ul className="divide-y">
          {tenants.map((tenant) => (
            <li
              key={tenant.id}
              className="flex items-center justify-between px-4 py-3"
            >
              <div>
                <div className="font-medium">{tenant.name}</div>
                <div className="text-xs text-muted-foreground">
                  {tenant.slug} · Created{' '}
                  {new Date(tenant.createdAt).toLocaleDateString()}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {tenant.agentRuntimeEnabled && (
                  <span className="rounded-full bg-emerald-900/40 px-2 py-0.5 text-[10px] font-medium text-emerald-200">
                    AI Enabled
                  </span>
                )}
              </div>
            </li>
          ))}
          {tenants.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-muted-foreground">
              {t('common.loading')}
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-bold">{value}</div>
    </div>
  );
}

function CreateTenantModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || !slug.trim() || !email.trim() || !password.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.post('/platform/tenants', {
        name: name.trim(),
        slug: slug.trim().toLowerCase(),
        adminEmail: email.trim(),
        adminPassword: password,
      });
      setName('');
      setSlug('');
      setEmail('');
      setPassword('');
      onCreated();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create tenant');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold">Create Tenant</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1">
            <label className="text-sm font-medium">
              Organization Name <span className="text-red-500">*</span>
            </label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">
              Slug <span className="text-red-500">*</span>
            </label>
            <Input
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              placeholder="acme-corp"
              required
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">
              Admin Email <span className="text-red-500">*</span>
            </label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">
              Admin Password <span className="text-red-500">*</span>
            </label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          {error && (
            <div className="rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Tenant'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
